// frontend/src/components/Articles/FeaturedHero.js
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

const PLACEHOLDER_IMG = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="600"><rect width="100%" height="100%" fill="#e5e7eb"/><text x="50%" y="50%" text-anchor="middle" fill="#6b7280" font-family="Inter, Arial, sans-serif" font-size="36">AI Digest</text></svg>');

export default function FeaturedHero({ articles }) {
  const mainArticle = articles[0];
  const secondaryArticles = articles.slice(1, 5);

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Feature Article */}
          {mainArticle && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-2 relative group cursor-pointer"
            >
              <Link to={`/article/${mainArticle.id}`}>
                <div className="relative h-[500px] rounded-xl overflow-hidden">
                  <img
                    src={mainArticle.image_url || PLACEHOLDER_IMG}
                    alt={mainArticle.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <div className="mb-3">
                      <span className="inline-block px-3 py-1 text-xs font-semibold text-white rounded-full bg-blue-600">
                        {typeof mainArticle.category === 'string' ? mainArticle.category : (mainArticle.category?.name || 'General')}
                      </span>
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                      {mainArticle.title}
                    </h1>
                    <p className="text-gray-200 text-lg mb-4 line-clamp-2">
                      {mainArticle.summary}
                    </p>
                    <div className="flex items-center text-gray-300 text-sm">
                      <span>{mainArticle.source}</span>
                      <span className="mx-2">•</span>
                      <span>
                        {formatDistanceToNow(new Date(mainArticle.published_date), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          )}

          {/* Secondary Features Grid */}
          <div className="lg:col-span-1 space-y-4">
            {secondaryArticles.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group cursor-pointer"
              >
                <Link to={`/article/${article.id}`}>
                  <div className="flex gap-4">
                    <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={article.image_url || PLACEHOLDER_IMG}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                      />
                    </div>
                    <div className="flex-1">
                      <span className="inline-block px-2 py-0.5 text-xs font-semibold text-white rounded-full mb-2 bg-blue-600">
                        {typeof article.category === 'string' ? article.category : (article.category?.name || 'General')}
                      </span>
                      <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-gray-500 text-xs mt-2">
                        {formatDistanceToNow(new Date(article.published_date), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
